// frontend/src/components/UI/PullToRefresh.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * PullToRefresh - Universal pull-to-refresh component for mobile and desktop.
 * 
 * Works on iOS PWA, Android, and desktop browsers by detecting touch/mouse
 * gestures and triggering a data refresh via React Query.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {React.RefObject} props.scrollContainerRef - Ref to the scrollable container
 * @param {Function} props.onRefresh - Optional custom refresh handler (async)
 * @param {number} props.threshold - Pull distance to trigger refresh (default: 80)
 * @param {boolean} props.disabled - Disable pull-to-refresh
 */
const PullToRefresh = ({ 
  children, 
  scrollContainerRef,
  onRefresh,
  threshold = 80,
  disabled = false 
}) => {
  const queryClient = useQueryClient();
  
  // State
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  
  // Refs for tracking touch
  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef(null);

  // Check if we're at the top of the scroll container
  const isAtTop = useCallback(() => {
    if (scrollContainerRef?.current) {
      return scrollContainerRef.current.scrollTop <= 0;
    }
    return true;
  }, [scrollContainerRef]);

  // Handle refresh action
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || disabled) return;
    
    setIsRefreshing(true);
    
    try {
      if (onRefresh) {
        // Custom refresh handler
        await onRefresh();
      } else {
        // Default: Invalidate all React Query caches
        await queryClient.invalidateQueries();
        // Small delay to show the spinner (feels more responsive)
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Pull-to-refresh error:', error);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [isRefreshing, disabled, onRefresh, queryClient]);

  // Touch handlers
  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing || !isAtTop()) return;
    
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [disabled, isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e) => {
    if (disabled || isRefreshing || !isPulling) return;
    
    currentY.current = e.touches[0].clientY;
    const delta = currentY.current - startY.current;
    
    // Only track downward pulls when at top
    if (delta > 0 && isAtTop()) {
      // Apply resistance (pull feels heavier as you go)
      const resistance = 0.5;
      const adjustedDelta = Math.min(delta * resistance, threshold * 1.5);
      setPullDistance(adjustedDelta);
      
      // Prevent default scroll when pulling
      if (adjustedDelta > 10) {
        e.preventDefault();
      }
    } else {
      setPullDistance(0);
    }
  }, [disabled, isRefreshing, isPulling, isAtTop, threshold]);

  const handleTouchEnd = useCallback(() => {
    if (disabled || isRefreshing) return;
    
    if (pullDistance >= threshold) {
      // Trigger refresh
      handleRefresh();
    } else {
      // Reset without refresh
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [disabled, isRefreshing, pullDistance, threshold, handleRefresh]);

  // Attach touch listeners to the scroll container
  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    // Use passive: false to allow preventDefault on touchmove
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scrollContainerRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Calculate visual states
  const showIndicator = pullDistance > 10 || isRefreshing;
  const isPastThreshold = pullDistance >= threshold;
  const rotation = Math.min((pullDistance / threshold) * 360, 360);
  const indicatorOpacity = Math.min(pullDistance / (threshold * 0.5), 1);

  return (
    <div ref={containerRef} className="relative">
      {/* Pull indicator */}
      <div 
        className={`
          absolute left-0 right-0 flex flex-col items-center justify-end
          transition-all duration-200 ease-out overflow-hidden z-10
          pointer-events-none
        `}
        style={{ 
          height: showIndicator ? Math.max(pullDistance, isRefreshing ? 60 : 0) : 0,
          opacity: showIndicator ? indicatorOpacity : 0,
          top: 0,
        }}
      >
        <div className="flex flex-col items-center pb-3">
          <RefreshCw 
            className={`
              w-6 h-6 text-blue-600 transition-transform
              ${isRefreshing ? 'animate-spin' : ''}
            `}
            style={{ 
              transform: isRefreshing ? undefined : `rotate(${rotation}deg)`,
            }}
          />
          <span className="text-xs text-gray-500 mt-1 font-medium">
            {isRefreshing 
              ? 'Refreshing...' 
              : isPastThreshold 
                ? 'Release to refresh' 
                : 'Pull to refresh'
            }
          </span>
        </div>
      </div>

      {/* Content wrapper - shifts down when pulling.
          Only apply transform while actively pulling/refreshing. An idle
          `translateY(0)` still creates a containing block for position:fixed
          descendants, which breaks viewport-anchored panels (e.g. Leave
          Calendar staff filter) and clips them against overflow-x-hidden. */}
      <div
        className="transition-transform duration-200 ease-out"
        style={
          showIndicator
            ? { transform: `translateY(${Math.max(pullDistance, isRefreshing ? 60 : 0)}px)` }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;

